import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";

type Source = {
  id: string;
  name: string;
  url: string;
  feed_url: string;
  published: boolean;
  sort_order: number;
};

const empty = { name: "", url: "", feed_url: "", published: true, sort_order: 0 };

const AdminNews = () => {
  const [sources, setSources] = useState<Source[]>([]);
  const [draft, setDraft] = useState<typeof empty>(empty);
  const [refreshing, setRefreshing] = useState(false);
  const [itemCount, setItemCount] = useState<number | null>(null);

  const load = async () => {
    const { data } = await supabase
      .from("news_sources")
      .select("*")
      .order("sort_order");
    setSources(data ?? []);
    const { count } = await supabase
      .from("news_items")
      .select("id", { count: "exact", head: true });
    setItemCount(count ?? 0);
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!draft.name || !draft.feed_url) {
      toast.error("Name and feed URL are required");
      return;
    }
    const { error } = await supabase.from("news_sources").insert(draft);
    if (error) return toast.error(error.message);
    toast.success("Source added");
    setDraft(empty);
    load();
  };

  const update = async (id: string, patch: Partial<Source>) => {
    const { error } = await supabase.from("news_sources").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this source and all its articles?")) return;
    const { error } = await supabase.from("news_sources").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data, error } = await supabase.functions.invoke("fetch-news");
      if (error) throw error;
      const summary = (data as any)?.summary ?? [];
      const inserted = summary.reduce((n: number, s: any) => n + (s.inserted ?? 0), 0);
      toast.success(`Fetched. ${inserted} new article${inserted === 1 ? "" : "s"}.`);
      const errors = summary.filter((s: any) => s.error);
      if (errors.length) {
        toast.error(`${errors.length} source${errors.length === 1 ? "" : "s"} failed — check Site Content`);
      }
      load();
    } catch (e: any) {
      toast.error(e.message ?? "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl">News sources</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {itemCount !== null && `${itemCount} articles in the feed · `}
            Auto-refresh runs every 30 minutes.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors disabled:opacity-50"
        >
          {refreshing ? "Refreshing…" : "Refresh feeds now"}
        </button>
      </div>

      {/* Add form */}
      <div className="bg-card rounded-2xl p-5 shadow-card mb-6">
        <h2 className="font-display text-xl mb-4">Add a publication</h2>
        <div className="grid md:grid-cols-4 gap-3">
          <input
            placeholder="Name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="px-3 py-2 rounded-lg bg-muted border border-border"
          />
          <input
            placeholder="Site URL (optional)"
            value={draft.url}
            onChange={(e) => setDraft({ ...draft, url: e.target.value })}
            className="px-3 py-2 rounded-lg bg-muted border border-border"
          />
          <input
            placeholder="RSS / Atom feed URL"
            value={draft.feed_url}
            onChange={(e) => setDraft({ ...draft, feed_url: e.target.value })}
            className="px-3 py-2 rounded-lg bg-muted border border-border md:col-span-2"
          />
        </div>
        <button
          onClick={add}
          className="mt-3 px-5 py-2 rounded-full bg-secondary text-secondary-foreground font-semibold"
        >
          Add source
        </button>
      </div>

      {/* List */}
      <div className="space-y-3">
        {sources.map((s) => (
          <div key={s.id} className="bg-card rounded-2xl p-4 shadow-card flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="font-display text-lg">{s.name}</span>
                {!s.published && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    Draft
                  </span>
                )}
              </div>
              <a
                href={s.feed_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground hover:text-primary truncate block"
              >
                {s.feed_url}
              </a>
            </div>
            <input
              type="number"
              value={s.sort_order}
              onChange={(e) =>
                update(s.id, { sort_order: parseInt(e.target.value) || 0 })
              }
              className="w-20 px-3 py-1.5 rounded-lg bg-muted border border-border text-sm"
            />
            <button
              onClick={() => update(s.id, { published: !s.published })}
              className="px-3 py-1.5 rounded-full bg-muted text-sm font-semibold"
            >
              {s.published ? "Unpublish" : "Publish"}
            </button>
            <button
              onClick={() => remove(s.id)}
              className="px-3 py-1.5 rounded-full bg-destructive text-destructive-foreground text-sm font-semibold"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
};

export default AdminNews;
